import { Controller } from "@hotwired/stimulus"
import { createElement } from "react"
import { createRoot } from "react-dom/client"

const REGISTRY = {}
const PENDING = []

export function registerReactComponents(components) {
  Object.assign(REGISTRY, components)

  // Mount any controllers that connected before their component was registered.
  // This handles Vite's ES module execution order where Stimulus may scan the
  // DOM before a later entrypoint has registered its components.
  const still_pending = []
  for (const entry of PENDING) {
    if (REGISTRY[entry.name]) {
      entry.mount()
    } else {
      still_pending.push(entry)
    }
  }
  PENDING.length = 0
  PENDING.push(...still_pending)
}

export function getReactComponent(name) {
  return REGISTRY[name]
}

// Stimulus controller that mounts a React component into its element.
//
// Usage in ERB (via react_component helper):
//   <div data-controller="react-island"
//        data-react-island-component-value="ShopSelect"
//        data-react-island-props-value='{"shopId": 1}'>
//   </div>
//
// The component must be registered via registerReactComponents() in a pack file.
export default class extends Controller {
  static values = {
    component: String,
    props: { type: Object, default: {} },
  }

  connect() {
    this._root = createRoot(this.element)
    this._mount()
  }

  disconnect() {
    if (this._root) {
      this._root.unmount()
      this._root = null
    }
  }

  _mount() {
    const ComponentClass = REGISTRY[this.componentValue]
    if (!ComponentClass) {
      // Component not yet registered — queue for when it arrives.
      PENDING.push({ name: this.componentValue, mount: () => this._mount() })
      setTimeout(() => {
        if (!REGISTRY[this.componentValue]) {
          console.warn(`[react-island] Component "${this.componentValue}" is not registered. Did you forget to call registerReactComponents()?`)
        }
      }, 5000)
      return
    }

    this._root.render(createElement(ComponentClass, this.propsValue))
  }
}
